import { useEffect, useRef, useState } from "react"

import { PromoPopupManager } from "../../../services/promo_popup_manager"
import { trackAnalyticsEvent } from "../../../services/track_analytics_event"
import { HotspotPicker, Notice } from "../../shared/components"
import { stripExtension } from "../../shared/services"
import { DownloadCard } from "../components/DownloadCard"
import { HotspotControls } from "../components/HotspotControls"
import { MoreOptions } from "../components/MoreOptions"
import { SourceBar } from "../components/SourceBar"
import { SourceNotices } from "../components/SourceNotices"
import { TryZone } from "../components/TryZone"
import { useCursorData } from "../contexts/CursorDataContext"
import { useCreateWindowsCursor } from "../hooks/mutations/useCreateWindowsCursor"
import { formatLabel } from "../services/formatLabel"
import { isSameUpload } from "../services/isSameUpload"
import { saveFile } from "../services/saveFile"

import type { CreateWindowsCursorParams } from "../api/WindowsCursorsApi.types"
import type { PreviewBackground } from "../types/PreviewBackground"
import type { Source } from "../types/Source"

interface Props {
  source: Source
  previewBackground: PreviewBackground
  onPreviewBackgroundChange: (previewBackground: PreviewBackground) => void
  onNextFile: () => void
}

export const StaticWorkbench = ({
  source,
  previewBackground,
  onPreviewBackgroundChange,
  onNextFile,
}: Props) => {
  const pickerRef = useRef<HTMLDivElement>(null)
  const { outputFrames, outputResampling, autoHotspot, setHotspot, setIsConverting } =
    useCursorData()
  const { hotspot } = source
  const [isFirstFrameNoticeDismissed, setIsFirstFrameNoticeDismissed] = useState(false)
  const createCursor = useCreateWindowsCursor()

  useEffect(() => {
    pickerRef.current?.focus({ preventScroll: true })
  }, [])

  const [frame] = outputFrames
  const params: CreateWindowsCursorParams = {
    frames: [frame.pixels.data],
    hotspotX: hotspot.x,
    hotspotY: hotspot.y,
    delayMs: source.delayMs,
    alternate: false,
    resampling: outputResampling,
    filename: source.filename,
  }
  const isCurrent =
    createCursor.variables !== undefined && isSameUpload(createCursor.variables, params)
  const result = createCursor.isSuccess && isCurrent ? createCursor.data : null
  const error = createCursor.isError && isCurrent ? createCursor.error : null

  const handleDownload = () => {
    setIsConverting(true)
    createCursor.mutate(params, {
      onSuccess: (file) => {
        saveFile(file)
        trackAnalyticsEvent("cursor_converter_download", {
          source_format: formatLabel(source.type),
          output_format: "CUR",
        })
        PromoPopupManager.recordActivity()
      },
      onSettled: () => setIsConverting(false),
    })
  }

  return (
    <div className="card gap-4 bg-base-200! p-4">
      <SourceBar
        source={source}
        frames={source.frames}
        previewBackground={previewBackground}
        isDone={Boolean(result)}
        isConverting={createCursor.isPending}
        onPreviewBackgroundChange={onPreviewBackgroundChange}
        onNextFile={onNextFile}
      />

      <SourceNotices />
      {source.isFirstFrameOnly && !isFirstFrameNoticeDismissed && (
        <Notice tone="warning" onDismiss={() => setIsFirstFrameNoticeDismissed(true)}>
          This browser can&apos;t read animation frames, so only the first frame was used. Chrome or
          Edge can do the full animation.
        </Notice>
      )}

      <div className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)]">
        {/* Locked while converting, so the saved file always matches the hotspot on screen. */}
        <div
          inert={createCursor.isPending}
          className={`flex flex-col gap-3 transition-opacity md:w-70 ${
            createCursor.isPending ? "opacity-60" : ""
          }`}
        >
          <div className="w-full max-w-70 self-center rounded-box bg-base-300 p-3">
            <HotspotPicker
              ref={pickerRef}
              label="Cursor preview. Click to set the hotspot, arrow keys to nudge it."
              imageUrl={frame.url}
              hotspot={hotspot}
              previewBackground={previewBackground}
              onChange={setHotspot}
            />
          </div>
          <p className="-mt-1 text-center text-xs font-semibold text-muted-content">
            {hotspot.x === autoHotspot.x && hotspot.y === autoHotspot.y
              ? "Auto hotspot — click to move it"
              : "Drag or use arrow keys to nudge"}
          </p>
          <HotspotControls hotspot={hotspot} autoHotspot={autoHotspot} onChange={setHotspot} />
          <MoreOptions />
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <TryZone
            frames={outputFrames}
            playback="loop"
            delayMs={source.delayMs}
            hotspot={hotspot}
            resampling={outputResampling}
            previewBackground={previewBackground}
          />
          <DownloadCard
            outputName={`${stripExtension(source.filename)}.cur`}
            frameCount={1}
            isPending={createCursor.isPending}
            result={result}
            error={error}
            onDownload={handleDownload}
            onDownloadAgain={saveFile}
          />
        </div>
      </div>
    </div>
  )
}
